/**
 * The back office's in-house i18n layer — deliberately the SAME shape as
 * `apps/portal/src/i18n`: a nested message object per locale, a `t()` that
 * walks a dot path and interpolates `{{name}}` tokens, and no dependency at
 * all (see CLAUDE.md's dependency budget). One pattern in this repo, not two.
 *
 * The one difference from the portal, and it matters: the portal owns its
 * locale in a module-level ref, while the back office already has one in the
 * `ui` store — which is what writes `<html lang>`/`<html dir>` and persists
 * the choice. This module READS that store and never holds a locale of its
 * own, so there is exactly one source of locale truth in the app.
 *
 * Usage in a component:
 *
 *     import { useI18n } from '@/i18n'
 *     const { t, locale } = useI18n()
 *     …
 *     <h1>{{ t('dashboard.exec.title') }}</h1>
 *     <p>{{ t('dashboard.exec.comparedWith', { range: previousLabel }) }}</p>
 *
 * Every user-facing string in this app MUST go through `t()`. A literal
 * English string in a template is a bug in an application that offers a
 * Dhivehi toggle — there is no linter for that, so it is a code-review
 * discipline, backed by `messages/completeness.spec.ts` for the mechanical
 * half (en and dv can never drift apart).
 */
import { type UiLocale } from '@fcms/ui'
import { computed, type ComputedRef } from 'vue'

import { useUiStore } from '@/stores/ui'

import dv from './messages/dv'
import en, { type MessageTree } from './messages/en'

export type BackofficeLocale = UiLocale

const messages: Record<BackofficeLocale, MessageTree> = { en, dv: dv as MessageTree }

/**
 * The active locale, read from the `ui` store on every call so that a `t()`
 * evaluated inside a render or a computed tracks it and re-runs on a change.
 *
 * The catch is not defensive noise, and it is not swallowing a real failure.
 * `useUiStore()` THROWS — it does not return undefined — when no Pinia has
 * been installed yet, and `t()` is legitimately reachable in exactly that
 * state: from a module-level constant table evaluated at import time, before
 * `main.ts` reaches `app.use(createPinia())`. English is the source-of-truth
 * locale and the only honest answer when there is no store to ask (and the
 * back office's default anyway). A genuine failure inside the store's own
 * setup cannot hide behind this: every other consumer of the store —
 * ThemeToggle, SiteSwitcher, both dashboards — calls it unguarded and would
 * surface the same error immediately.
 *
 * `getActivePinia()` would be the tidier test, but it is implemented as an
 * `inject()` with no default, so calling it during a child component's render
 * logs a Vue injection warning on every translated string in any test that
 * mounts without the plugin.
 */
function activeLocale(): BackofficeLocale {
  try {
    return useUiStore().locale
  } catch {
    return 'en'
  }
}

function resolvePath(tree: unknown, path: string[]): unknown {
  let node: unknown = tree
  for (const segment of path) {
    if (typeof node !== 'object' || node === null) return undefined
    node = (node as Record<string, unknown>)[segment]
  }
  return node
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => {
    const value = params[key]
    return value === undefined ? match : String(value)
  })
}

/**
 * Translates a dot-path key, e.g.
 * `t('dashboard.ops.depthServing', { count: 3 })`.
 *
 * Falls back to English, then to the raw key, so a missing translation is
 * visibly a placeholder rather than a blank label — and is greppable in a
 * screenshot review.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const path = key.split('.')
  const own = resolvePath(messages[activeLocale()], path)
  if (typeof own === 'string') return interpolate(own, params)
  const fallback = resolvePath(messages.en, path)
  if (typeof fallback === 'string') return interpolate(fallback, params)
  return key
}

/**
 * Whether a key exists at all, in English.
 *
 * For the handful of places that translate an identifier the app does not
 * own — a route name from `router/nav.ts`, a role id from the API — and that
 * carry an English fallback of their own. `t()` cannot express "no
 * translation" (by design: it returns the key so the gap is visible), so
 * those callers ask first. Do NOT use this to build optional prose; a string
 * a screen shows belongs in the message tree.
 */
export function hasMessage(key: string): boolean {
  return typeof resolvePath(messages.en, key.split('.')) === 'string'
}

/**
 * Called once from `main.ts`, after Pinia is installed and before mount.
 *
 * Constructing the `ui` store here is the whole job: its setup applies the
 * persisted locale through `@fcms/ui`'s `setLocale`, so the very first paint
 * is already in the right script and direction. Without it the store is not
 * built until the shell renders, and `t()` would answer in English for the
 * first frame of a Dhivehi session.
 */
export function initI18n(): void {
  useUiStore()
}

export function useI18n(): {
  t: typeof t
  locale: ComputedRef<BackofficeLocale>
  setLocale: (value: BackofficeLocale) => void
  toggleLocale: () => void
} {
  const ui = useUiStore()
  return {
    t,
    locale: computed(() => ui.locale),
    setLocale: (value: BackofficeLocale) => ui.applyLocale(value),
    toggleLocale: () => ui.toggleLocale(),
  }
}
